import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, RotateCcw, TerminalSquare, Lightbulb, Trophy,
  BookOpen, X, ExternalLink, Flag, ChevronLeft, ChevronRight, Copy, Check,
  StickyNote, Clock, Boxes, Layers, ShieldCheck, ListChecks,
} from 'lucide-react';
import { ClusterEngine } from '@/lib/k8s-sim/engine';
import { LABS, getLabsForCert, type Lab } from '@/lib/k8s-sim/labs';
import { Terminal, type TerminalLine } from '@/components/lab/Terminal';
import { InstructionText } from '@/components/lab/InstructionText';
import { LabEditor } from '@/components/lab/LabEditor';
import { EXAM_DOCS, ALLOWED_DOMAINS } from '@/lib/k8s-sim/docs';
import { useDocumentTitle } from '@/lib/useDocumentTitle';

// Complétion Tab (comme la complétion bash activée à l'examen).
const SHELL_COMMANDS = ['kubectl', 'k', 'vim', 'vi', 'nano', 'cat', 'ls', 'cd', 'echo', 'grep', 'head', 'tail', 'wc', 'pwd', 'mkdir', 'touch', 'rm', 'cp', 'mv', 'clear', 'help', 'whoami', 'env'];
const KUBECTL_VERBS = ['get', 'create', 'apply', 'run', 'scale', 'delete', 'expose', 'label', 'set', 'rollout', 'describe', 'explain', 'config', 'version'];
const KUBECTL_RESOURCES = ['pods', 'deployments', 'services', 'configmaps', 'secrets', 'serviceaccounts', 'namespaces', 'roles', 'rolebindings', 'clusterroles', 'clusterrolebindings', 'networkpolicies'];
const FILE_ARG_CMDS = new Set(['cat', 'vim', 'vi', 'nano', 'less', 'more', 'head', 'tail', 'rm', 'cp', 'mv', 'grep', 'wc']);

// Charte visuelle par certification (badge, accent, icône) — thème clair/sombre.
const LEVEL_META: Record<Lab['level'], {
  label: string;
  icon: typeof Boxes;
  badge: string;
  accent: string;
  ring: string;
  text: string;
  dot: string;
}> = {
  CKAD: {
    label: 'Application Developer',
    icon: Boxes,
    badge: 'bg-sky-500/10 text-sky-600 dark:text-sky-400 ring-sky-500/20',
    accent: 'from-sky-500 to-cyan-500',
    ring: 'hover:ring-sky-500/40',
    text: 'group-hover:text-sky-600 dark:group-hover:text-sky-400',
    dot: 'bg-sky-500',
  },
  CKA: {
    label: 'Administrator',
    icon: Layers,
    badge: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 ring-violet-500/20',
    accent: 'from-violet-500 to-fuchsia-500',
    ring: 'hover:ring-violet-500/40',
    text: 'group-hover:text-violet-600 dark:group-hover:text-violet-400',
    dot: 'bg-violet-500',
  },
  CKS: {
    label: 'Security Specialist',
    icon: ShieldCheck,
    badge: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 ring-rose-500/20',
    accent: 'from-rose-500 to-orange-500',
    ring: 'hover:ring-rose-500/40',
    text: 'group-hover:text-rose-600 dark:group-hover:text-rose-400',
    dot: 'bg-rose-500',
  },
};
const LEVEL_ORDER: Lab['level'][] = ['CKAD', 'CKA', 'CKS'];

function commonPrefix(arr: string[]): string {
  if (arr.length === 0) return '';
  let p = arr[0];
  for (const s of arr) while (!s.startsWith(p)) p = p.slice(0, -1);
  return p;
}

/** Durée officielle des examens (2 h) → chrono décompté comme au réel. */
const EXAM_MINUTES: Record<string, number> = { CKAD: 120, CKA: 120, CKS: 120 };

function fmtClock(totalSec: number): string {
  const clamped = Math.max(0, totalSec);
  const h = Math.floor(clamped / 3600);
  const m = Math.floor((clamped % 3600) / 60);
  const s = clamped % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

const WELCOME: TerminalLine[] = [
  { kind: 'output', text: 'Simulateur kubectl — bac à sable (100 % navigateur).' },
  { kind: 'output', text: "Tape 'help' pour la liste des commandes. ↑/↓ = historique." },
  { kind: 'output', text: '' },
];

export function LabPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const certSlug = searchParams.get('cert');
  useDocumentTitle('Examen pratique — Lab');

  const availableLabs = useMemo(() => (certSlug ? getLabsForCert(certSlug) : LABS), [certSlug]);

  const [activeLab, setActiveLab] = useState<Lab | null>(null);
  const [lines, setLines] = useState<TerminalLine[]>(WELCOME);
  const engineRef = useRef(new ClusterEngine());
  const [snapshotVersion, setSnapshotVersion] = useState(0);
  const [showDocs, setShowDocs] = useState(false);
  const [showNotepad, setShowNotepad] = useState(false);
  const [notepad, setNotepad] = useState('');
  // Éditeur plein écran (vim/vi/nano) ouvert sur un fichier du VFS.
  const [editor, setEditor] = useState<{ file: string; content: string; mode: 'vim' | 'nano' } | null>(null);
  const [focusSignal, setFocusSignal] = useState(0);

  // Navigation type examen : une « question » = une tâche du lab.
  const [taskIndex, setTaskIndex] = useState(0);
  const [flagged, setFlagged] = useState<Set<number>>(new Set());
  const [copied, setCopied] = useState(false);

  // Chrono décompté (comme au réel).
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!activeLab) return;
    const total = (EXAM_MINUTES[activeLab.level] ?? 120) * 60;
    setRemaining(total);
    const id = window.setInterval(() => setRemaining((r) => (r > 0 ? r - 1 : 0)), 1000);
    return () => window.clearInterval(id);
  }, [activeLab]);

  // Diviseur redimensionnable (largeur du panneau « Content », en %).
  const [leftPct, setLeftPct] = useState(42);
  const splitRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const h = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);
  useEffect(() => {
    const move = (e: MouseEvent) => {
      if (!draggingRef.current || !splitRef.current) return;
      const rect = splitRef.current.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      setLeftPct(Math.min(70, Math.max(24, pct)));
    };
    const up = () => { draggingRef.current = false; document.body.style.cursor = ''; };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, []);

  const startLab = (lab: Lab) => {
    engineRef.current.reset();
    // Ressources déjà en place supposées par le scénario (ex. StatefulSet à
    // réduire, pods existants à isoler) — appliquées à l'état du cluster.
    (lab.seed ?? []).forEach((m) => engineRef.current.seed(m));
    // Manifestes fournis avec le lab (ex. NetworkPolicy à appliquer).
    (lab.files ?? []).forEach((f) => engineRef.current.seedFile(f.path, f.content));
    setActiveLab(lab);
    setTaskIndex(0);
    setFlagged(new Set());
    setNotepad('');
    setLines([
      { kind: 'output', text: `Cluster prêt — contexte « k8s-${lab.level.toLowerCase()} ».` },
      ...WELCOME.slice(0, 2),
      { kind: 'output', text: '' },
    ]);
    setSnapshotVersion((v) => v + 1);
  };

  // Ouverture directe d'un examen via ?lab=<id> (depuis la page de détail de la
  // certif, qui liste les examens de simulation). Ne se déclenche qu'une fois.
  const autoOpened = useRef(false);
  useEffect(() => {
    if (autoOpened.current || activeLab) return;
    const labId = searchParams.get('lab');
    if (!labId) return;
    const lab = availableLabs.find((l) => l.id === labId);
    if (lab) { autoOpened.current = true; startLab(lab); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, availableLabs, activeLab]);

  const handleCommand = useCallback((cmd: string) => {
    const trimmed = cmd.trim();
    // Éditeurs plein écran : vim / vi / nano [fichier].
    const edit = trimmed.match(/^(vim|vi|nano)(?:\s+(\S+))?\s*$/i);
    if (edit) {
      setLines((prev) => [...prev, { kind: 'input', text: cmd }]);
      const tool = edit[1].toLowerCase() === 'nano' ? 'nano' : 'vim';
      const fileArg = edit[2];
      if (!fileArg) {
        setLines((prev) => [...prev, { kind: 'output', text: `usage : ${edit[1]} <fichier>` }]);
        return;
      }
      const content = engineRef.current.readFile(fileArg) ?? '';
      setEditor({ file: fileArg, content, mode: tool });
      return;
    }
    // `kubectl config use-context …` : intercepté comme au réel (une seule
    // cluster simulée) → message de bascule, sans passer par le moteur.
    if (/^kubectl\s+config\s+use-context\b/i.test(trimmed)) {
      const ctx = trimmed.split(/\s+/).pop();
      setLines((prev) => [...prev, { kind: 'input', text: cmd }, { kind: 'output', text: `Switched to context "${ctx}".` }]);
      return;
    }
    const res = trimmed ? engineRef.current.exec(trimmed) : null;
    setLines((prev) => {
      if (res && res.text === '__CLEAR__') return [];
      const next: TerminalLine[] = [...prev, { kind: 'input', text: cmd }];
      if (res && res.text) next.push({ kind: res.isError ? 'error' : 'output', text: res.text });
      return next;
    });
    setSnapshotVersion((v) => v + 1);
  }, []);

  // Complétion Tab contextuelle (commandes, verbes, ressources, namespaces, fichiers).
  const handleComplete = useCallback((input: string): string => {
    const endsSpace = /\s$/.test(input);
    const tokens = input.split(/\s+/).filter(Boolean);
    const partial = endsSpace ? '' : (tokens[tokens.length - 1] ?? '');
    const prior = endsSpace ? tokens : tokens.slice(0, -1);
    const first = prior[0];
    const prev = prior[prior.length - 1];
    const isKubectl = first === 'kubectl' || first === 'k';

    let candidates: string[] = [];
    if (prior.length === 0) candidates = SHELL_COMMANDS;
    else if (prev === '-n' || prev === '--namespace') candidates = engineRef.current.snapshot().namespaces.map((n) => n.name);
    else if (prev === '-f' || prev === '--filename') candidates = engineRef.current.listFiles();
    else if (isKubectl && prior.length === 1) candidates = KUBECTL_VERBS;
    else if (isKubectl && prior.length === 2 && ['get', 'describe', 'delete', 'edit'].includes(prior[1])) candidates = KUBECTL_RESOURCES;
    else if (first && FILE_ARG_CMDS.has(first)) candidates = engineRef.current.listFiles();
    else candidates = engineRef.current.listFiles();

    const matches = candidates.filter((c) => c.startsWith(partial));
    if (matches.length === 0) return input;
    const base = endsSpace ? input : input.slice(0, input.length - partial.length);
    if (matches.length === 1) return base + matches[0] + (matches[0].endsWith('/') ? '' : ' ');
    const cp = commonPrefix(matches);
    if (cp.length > partial.length) return base + cp;
    // Plusieurs correspondances de même préfixe → on les liste (comme bash).
    setLines((prev2) => [...prev2, { kind: 'input', text: input }, { kind: 'output', text: matches.join('   ') }]);
    return input;
  }, []);

  const snapshot = useMemo(() => engineRef.current.snapshot(), [snapshotVersion]);
  const taskStatus = useMemo(
    () => (activeLab ? activeLab.tasks.map((t) => ({ id: t.id, done: t.validate(snapshot) })) : []),
    [activeLab, snapshot],
  );
  const completed = taskStatus.filter((t) => t.done).length;
  const total = taskStatus.length;
  const allDone = total > 0 && completed === total;

  const copyContext = (ctxCmd: string) => {
    navigator.clipboard?.writeText(ctxCmd).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  // ─────────────────────────── Écran de sélection ───────────────────────────
  if (!activeLab) {
    const grouped = LEVEL_ORDER
      .map((lvl) => ({ lvl, labs: availableLabs.filter((l) => l.level === lvl) }))
      .filter((g) => g.labs.length > 0);

    return (
      <div className="h-full overflow-y-auto bg-app px-4 py-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-sm text-fg-subtle hover:text-fg mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>

          {/* En-tête */}
          <div className="relative overflow-hidden rounded-2xl border border-line bg-gradient-to-br from-emerald-500/10 via-surface to-surface p-6 sm:p-7 mb-8">
            <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-emerald-500/10 blur-2xl" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-2">
                <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-emerald-500/15 text-emerald-500">
                  <TerminalSquare className="w-6 h-6" />
                </span>
                <div>
                  <h1 className="text-2xl font-bold text-fg leading-tight">Examen pratique</h1>
                  <p className="text-xs text-fg-subtle">{availableLabs.length} épreuves hands-on · CKAD · CKA · CKS</p>
                </div>
              </div>
              <p className="text-sm text-fg-muted max-w-2xl mt-3">
                Un environnement fidèle à l'examen réel : problématique de production à gauche, terminal{' '}
                <code className="text-emerald-500 font-mono">kubectl</code> à droite, chrono décompté et
                documentation autorisée. Chaque épreuve est notée tâche par tâche.
              </p>
            </div>
          </div>

          {availableLabs.length === 0 && (
            <div className="p-8 rounded-xl bg-surface border border-line text-center">
              <p className="text-sm text-fg-muted font-medium">Épreuves en préparation pour cette certification.</p>
            </div>
          )}

          {/* Épreuves regroupées par certification */}
          <div className="space-y-9">
            {grouped.map(({ lvl, labs }) => {
              const meta = LEVEL_META[lvl];
              const Icon = meta.icon;
              return (
                <section key={lvl}>
                  <div className="flex items-center gap-2.5 mb-4">
                    <span className={`flex items-center justify-center w-8 h-8 rounded-lg ring-1 ${meta.badge}`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <div>
                      <h2 className="text-sm font-bold text-fg leading-none">{lvl}</h2>
                      <span className="text-[11px] text-fg-subtle">{meta.label}</span>
                    </div>
                    <span className="ml-auto text-xs text-fg-subtle tabular-nums">{labs.length} épreuve{labs.length > 1 ? 's' : ''}</span>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    {labs.map((lab) => (
                      <button
                        key={lab.id}
                        onClick={() => startLab(lab)}
                        className={`group relative text-left overflow-hidden rounded-xl bg-surface ring-1 ring-line ${meta.ring} hover:shadow-lg hover:shadow-black/5 hover:-translate-y-0.5 transition-all duration-200`}
                      >
                        <span className={`absolute left-0 inset-y-0 w-1 bg-gradient-to-b ${meta.accent}`} />
                        <div className="pl-5 pr-4 py-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ring-1 ${meta.badge}`}>{lab.level}</span>
                            <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-fg-subtle">
                              <ListChecks className="w-3.5 h-3.5" />
                              {lab.tasks.length} tâche{lab.tasks.length > 1 ? 's' : ''}
                            </span>
                          </div>
                          <h3 className={`text-[15px] font-semibold text-fg ${meta.text} transition-colors`}>{lab.title}</h3>
                          <p className="text-[13px] text-fg-subtle mt-1 line-clamp-2">{lab.summary}</p>
                          <span className={`mt-3 inline-flex items-center gap-1 text-xs font-medium text-fg-subtle ${meta.text} transition-colors`}>
                            Commencer l'épreuve
                            <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────── Environnement d'examen ───────────────────────
  const task = activeLab.tasks[taskIndex];
  const taskDone = taskStatus[taskIndex]?.done;
  const weight = Math.round(100 / total);
  const examContext = `k8s-${activeLab.level.toLowerCase()}`;
  const contextCmd = `kubectl config use-context ${examContext}`;
  const lowTime = remaining <= 300;

  const toggleFlag = () => {
    setFlagged((prev) => {
      const next = new Set(prev);
      if (next.has(taskIndex)) next.delete(taskIndex); else next.add(taskIndex);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-[#0f172a] text-slate-100">
      {/* ── Barre supérieure (chrome examen) ── */}
      <div className="flex-shrink-0 bg-[#0b1220] border-b border-slate-700/70">
        <div className="flex items-center justify-between px-3 sm:px-4 h-12">
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={() => setActiveLab(null)} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-100 px-2 py-1 rounded hover:bg-white/5">
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Quitter</span>
            </button>
            <span className="hidden md:inline text-xs font-semibold px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300">{activeLab.level}</span>
            <span className="text-sm font-medium text-slate-200 truncate hidden lg:block">{activeLab.title}</span>
          </div>

          {/* Navigateur de question */}
          <div className="flex items-center gap-1.5">
            <button onClick={() => setTaskIndex((i) => Math.max(0, i - 1))} disabled={taskIndex === 0} className="p-1.5 rounded text-slate-300 hover:bg-white/5 disabled:opacity-30" title="Question précédente">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs sm:text-sm text-slate-200 tabular-nums whitespace-nowrap">
              Question <strong>{taskIndex + 1}</strong> / {total}
            </span>
            <button onClick={() => setTaskIndex((i) => Math.min(total - 1, i + 1))} disabled={taskIndex >= total - 1} className="p-1.5 rounded text-slate-300 hover:bg-white/5 disabled:opacity-30" title="Question suivante">
              <ChevronRight className="w-4 h-4" />
            </button>
            <button onClick={toggleFlag} className={`ml-1 p-1.5 rounded ${flagged.has(taskIndex) ? 'text-amber-400 bg-amber-400/10' : 'text-slate-400 hover:bg-white/5'}`} title="Marquer pour révision">
              <Flag className="w-4 h-4" fill={flagged.has(taskIndex) ? 'currentColor' : 'none'} />
            </button>
          </div>

          {/* Chrono + outils */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <span className={`flex items-center gap-1.5 tabular-nums font-mono text-sm px-2 py-1 rounded ${lowTime ? 'text-red-300 bg-red-500/15 animate-pulse' : 'text-slate-100 bg-white/5'}`} title="Temps restant">
              <Clock className="w-3.5 h-3.5" /> {fmtClock(remaining)}
            </span>
            <button onClick={() => setShowNotepad(true)} className="hidden sm:flex items-center gap-1 text-xs text-slate-300 hover:text-white px-2 py-1 rounded hover:bg-white/5" title="Bloc-notes">
              <StickyNote className="w-4 h-4" />
            </button>
            <button onClick={() => setShowDocs(true)} className="flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200 px-2 py-1 rounded hover:bg-emerald-500/10" title="Documentation autorisée">
              <BookOpen className="w-4 h-4" /> <span className="hidden sm:inline">Docs</span>
            </button>
            <button onClick={() => startLab(activeLab)} className="flex items-center gap-1 text-xs text-slate-300 hover:text-white px-2 py-1 rounded hover:bg-white/5" title="Réinitialiser">
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Pastilles de questions (état : courante / résolue / marquée) */}
        <div className="flex items-center gap-1 px-3 sm:px-4 pb-2 overflow-x-auto scrollbar-hide">
          {activeLab.tasks.map((t, i) => {
            const done = taskStatus[i]?.done;
            const cur = i === taskIndex;
            const flag = flagged.has(i);
            return (
              <button
                key={t.id}
                onClick={() => setTaskIndex(i)}
                className={`relative w-7 h-7 flex-shrink-0 rounded text-[11px] font-bold transition-colors ${
                  cur ? 'bg-emerald-500 text-white'
                  : done ? 'bg-emerald-500/20 text-emerald-300'
                  : 'bg-white/5 text-slate-400 hover:bg-white/10'
                }`}
                title={`Question ${i + 1}${done ? ' — résolue' : ''}${flag ? ' — marquée' : ''}`}
              >
                {i + 1}
                {flag && <Flag className="w-2.5 h-2.5 text-amber-400 absolute -top-1 -right-1" fill="currentColor" />}
              </button>
            );
          })}
          <span className="ml-auto text-[11px] text-slate-400 whitespace-nowrap pl-3">{completed}/{total} résolues</span>
        </div>
      </div>

      {/* ── Corps : Content (clair) | terminal (sombre) ── */}
      <div ref={splitRef} className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Panneau consigne (clair, comme l'examen réel) */}
        <div
          className="w-full h-1/2 lg:h-full overflow-y-auto bg-white text-slate-800 flex-shrink-0"
          style={isDesktop ? { width: `${leftPct}%` } : undefined}
        >
          <div>
            <div className="px-5 py-5 max-w-none">
              {/* Problématique du lab (2-3 paragraphes) — contexte type examen.
                  Affichée UNIQUEMENT sur la 1re question (au démarrage de l'examen). */}
              {activeLab.scenario && taskIndex === 0 && (
                <div className="mb-5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
                    Problématique — {activeLab.title}
                  </p>
                  <div className="text-[13.5px] leading-relaxed text-slate-700 whitespace-pre-wrap">
                    {activeLab.scenario}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900">Question {taskIndex + 1}</h2>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold px-2 py-1 rounded bg-slate-100 text-slate-600">Task weight: {weight}%</span>
                  {taskDone && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded bg-emerald-100 text-emerald-700">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Résolu
                    </span>
                  )}
                </div>
              </div>

              {/* Encart contexte (jaune) — signature de l'examen réel */}
              <div className="mb-5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5">
                <p className="text-xs text-amber-800 mb-1.5">
                  Définissez le contexte de configuration :
                </p>
                <div className="flex items-center gap-2 bg-white border border-amber-200 rounded px-2.5 py-1.5">
                  <code className="flex-1 text-[12.5px] text-slate-800 font-mono break-all">{contextCmd}</code>
                  <button onClick={() => copyContext(contextCmd)} className="flex-shrink-0 text-amber-700 hover:text-amber-900" title="Copier">
                    {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Consigne (format examen : puces + chips de code + blocs) */}
              <InstructionText text={task.instruction} />

              {/* Indice repliable */}
              {task.hint && (
                <details className="mt-5 group">
                  <summary className="inline-flex items-center gap-1.5 text-sm text-slate-500 cursor-pointer list-none hover:text-slate-700">
                    <Lightbulb className="w-4 h-4" /> Afficher un indice
                  </summary>
                  <code className="block mt-2 text-[13px] text-slate-700 bg-slate-100 rounded px-3 py-2 break-all font-mono">{task.hint}</code>
                </details>
              )}

              {allDone && (
                <div className="mt-6 rounded-lg bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3">
                  <Trophy className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">Épreuve réussie !</p>
                    <p className="text-xs text-emerald-700">Toutes les questions sont validées.</p>
                  </div>
                </div>
              )}

              {/* Navigation bas de consigne */}
              <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
                <button onClick={() => setTaskIndex((i) => Math.max(0, i - 1))} disabled={taskIndex === 0} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-30">
                  <ChevronLeft className="w-4 h-4" /> Précédente
                </button>
                <button onClick={toggleFlag} className={`inline-flex items-center gap-1.5 text-sm px-2.5 py-1 rounded ${flagged.has(taskIndex) ? 'text-amber-700 bg-amber-100' : 'text-slate-500 hover:bg-slate-100'}`}>
                  <Flag className="w-3.5 h-3.5" fill={flagged.has(taskIndex) ? 'currentColor' : 'none'} /> Marquer
                </button>
                <button onClick={() => setTaskIndex((i) => Math.min(total - 1, i + 1))} disabled={taskIndex >= total - 1} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-30">
                  Suivante <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Diviseur (desktop) */}
        <div
          onMouseDown={() => { draggingRef.current = true; document.body.style.cursor = 'col-resize'; }}
          className="hidden lg:block w-1.5 flex-shrink-0 cursor-col-resize bg-slate-700 hover:bg-emerald-500 transition-colors"
          title="Glisser pour redimensionner"
        />

        {/* Terminal (sombre, bord-à-bord) */}
        <div className="flex-1 min-h-0 h-1/2 lg:h-auto">
          <Terminal
            lines={lines}
            onCommand={handleCommand}
            onComplete={handleComplete}
            focusSignal={focusSignal}
            prompt="$"
            flush
            title={`candidate@${examContext}:~$`}
          />
        </div>
      </div>

      {/* ── Bloc-notes ── */}
      {showNotepad && (
        <div className="fixed inset-0 z-[70] flex justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowNotepad(false)} />
          <div className="relative w-full max-w-sm bg-[#0f172a] border-l border-slate-700 h-full flex flex-col shadow-2xl">
            <div className="px-5 py-4 border-b border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-2"><StickyNote className="w-5 h-5 text-amber-300" /><h2 className="text-sm font-semibold text-slate-100">Bloc-notes</h2></div>
              <button onClick={() => setShowNotepad(false)} className="text-slate-400 hover:text-white p-1 rounded hover:bg-white/5"><X className="w-5 h-5" /></button>
            </div>
            <textarea
              value={notepad}
              onChange={(e) => setNotepad(e.target.value)}
              placeholder="Notes temporaires (effacées à la fin de l'épreuve)…"
              className="flex-1 bg-transparent text-slate-200 text-sm p-5 outline-none resize-none font-mono placeholder-slate-500"
            />
          </div>
        </div>
      )}

      {/* ── Documentation autorisée ── */}
      {showDocs && (
        <div className="fixed inset-0 z-[70] flex justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDocs(false)} />
          <div className="relative w-full max-w-md bg-[#0f172a] border-l border-slate-700 h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-[#0f172a] border-b border-slate-700 px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-emerald-300" /><h2 className="text-sm font-semibold text-slate-100">Documentation autorisée</h2></div>
              <button onClick={() => setShowDocs(false)} className="text-slate-400 hover:text-white p-1 rounded hover:bg-white/5"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-5 py-4">
              <p className="text-xs text-slate-400 mb-4 leading-relaxed">
                Pendant l'examen réel, seuls ces domaines sont accessibles :{' '}
                {ALLOWED_DOMAINS.map((d) => (<code key={d} className="text-emerald-300 mr-1">{d}</code>))}. Les liens s'ouvrent dans un nouvel onglet.
              </p>
              <div className="space-y-5">
                {EXAM_DOCS.map((grp) => (
                  <div key={grp.group}>
                    <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">{grp.group}</h3>
                    <div className="space-y-1.5">
                      {grp.links.map((link) => (
                        <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-white/5 border border-slate-700 hover:border-emerald-500/40 text-sm text-slate-300 hover:text-white transition-colors group">
                          <span>{link.label}</span>
                          <ExternalLink className="w-3.5 h-3.5 text-slate-500 group-hover:text-emerald-300 flex-shrink-0" />
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Éditeur plein écran (vim / vi / nano) ── */}
      {editor && (
        <LabEditor
          file={editor.file}
          initialContent={editor.content}
          mode={editor.mode}
          onWrite={(content) => engineRef.current.seedFile(editor.file, content)}
          onExit={(message) => {
            setEditor(null);
            if (message) setLines((prev) => [...prev, { kind: 'output', text: message }]);
            setFocusSignal((f) => f + 1);
          }}
        />
      )}
    </div>
  );
}
